import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, Download } from "lucide-react";
import Seo from "@/components/Seo";
import { BLOG_POSTS } from "@/lib/blog-posts";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Blog", path: "/blog" },
];

export default function Blog() {
  return (
    <div data-testid="blog-index-page">
      <Seo
        path="/blog"
        title="Travel Insights & DMC Resources | Trawelldmc Blog"
        description="Expert insights on MICE, incentive travel, corporate group travel, and destination guides from Trawelldmc — 20 years of DMC expertise."
        breadcrumb={BREADCRUMB}
      />

      {/* HERO */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">The Trawelldmc Journal</div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.04]">
              Travel Insights <br />
              & <span className="text-[#FF5238]">Resources.</span>
            </h1>
            <p className="text-white/70 mt-8 text-lg max-w-2xl leading-relaxed">
              Expert thinking on MICE, incentive travel, corporate group
              programmes, and destination strategy — written by the
              Trawelldmc team. Two decades of operating experience, distilled
              into actionable insights.
            </p>
          </div>
        </div>
      </section>

      {/* LEAD MAGNET STRIP */}
      <section className="bg-[#FDFCF8] border-b border-slate-200" data-testid="blog-lead-magnet-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-2">Free download · 10-page PDF</div>
            <h2 className="font-heading text-2xl md:text-3xl font-light tracking-tight text-[#0B192C]">
              All 5 MICE case studies in <span className="text-[#FF5238]">one printable PDF.</span>
            </h2>
            <p className="text-slate-600 mt-2 text-sm md:text-base leading-relaxed max-w-xl">
              Dubai · Singapore · Bali — plus Asia 2026 MICE trends and a
              practical RFP guide. Send it to your procurement team.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/mice-case-studies-2025" className="btn-primary inline-flex" data-testid="blog-lead-magnet-cta">
              Get the PDF <Download className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* POSTS GRID */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14">
          {BLOG_POSTS.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group block"
              data-testid={`blog-card-${post.slug}`}
            >
              <article className="card-flat overflow-hidden h-full flex flex-col">
                <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                  <img
                    src={post.image}
                    alt={`${post.title} — Trawelldmc Blog`}
                    width="1600"
                    height="1000"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <div className="overline mb-4">{post.category}</div>
                  <h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-[#0B192C] leading-tight group-hover:text-[#FF5238] transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-slate-600 mt-4 leading-relaxed flex-1">
                    {post.excerpt}
                  </p>
                  <div className="mt-6 flex items-center gap-4 text-sm text-slate-500">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      {post.publishedLabel}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      {post.readTime}
                    </span>
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 text-[#FF5238] font-medium">
                    Read article
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FDFCF8] border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C]">
            Have a programme to plan?
          </h2>
          <p className="text-slate-600 mt-5 max-w-xl mx-auto">
            Talk to a senior account manager. We respond within 24 hours.
          </p>
          <div className="mt-8">
            <Link to="/inquiry" className="btn-primary" data-testid="blog-cta-contact">
              Request a proposal <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
